import { apiTranslations } from './domains/api';
import { authTranslations } from './domains/auth';
import { allocationsTranslations } from './domains/allocations';
import { bankTranslations } from './domains/bank';
import { adminTranslations } from './domains/admin';
import { anomaliesTranslations } from './domains/anomalies';
import { auditTranslations } from './domains/audit';
import { commonTranslations } from './domains/common';
import { correctionsTranslations } from './domains/corrections';
import { matchingTranslations } from './domains/matching';
import { paymentCandidatesTranslations, transactionsTranslations } from './domains/paymentCandidates';
import { platformTranslations } from './domains/platform';
import { settingsTranslations } from './domains/settings';
import { sharedTranslations } from './domains/shared';
import { statusTranslations } from './domains/status';
import { syrveTranslations } from './domains/syrve';
import { workbenchV1Translations } from './domains/workbenchV1';
import { workbenchV2Translations } from './domains/workbenchV2';

export const supportedLocales = ['en', 'id', 'ru'] as const;

export type Locale = typeof supportedLocales[number];

type BaseTranslationKey =
  | 'app.loading'
  | 'app.language.label'
  | 'app.language.ariaLabel'
  | 'app.language.ru'
  | 'app.language.en'
  | 'app.language.id'
  | 'layout.context.empty'
  | 'layout.context.organization'
  | 'layout.context.outlet'
  | 'layout.context.noOutlet'
  | 'layout.context.noOutletsInOrg'
  | 'layout.context.compactLabel'
  | 'layout.context.selectOrganization'
  | 'layout.context.selectOutlet'
  | 'layout.nav.operations'
  | 'layout.nav.dashboard'
  | 'layout.nav.accountantWorkbench'
  | 'layout.nav.bankStatements'
  | 'layout.nav.nameMatching'
  | 'layout.nav.counterpartyRules'
  | 'layout.nav.bankTemplates'
  | 'layout.nav.manualCorrections'
  | 'layout.nav.auditLog'
  | 'layout.nav.syrveReconciliation'
  | 'layout.nav.syrveDocuments'
  | 'layout.nav.syrveUnpaid'
  | 'layout.nav.syrvePartiallyPaid'
  | 'layout.nav.syrvePaid'
  | 'layout.nav.allocation'
  | 'layout.nav.allocations'
  | 'layout.nav.allocationReview'
  | 'layout.nav.paymentPosting'
  | 'layout.nav.advanced'
  | 'layout.nav.postingQueue'
  | 'layout.nav.paymentAnomalies'
  | 'layout.nav.reconciliation'
  | 'layout.nav.hub.imports'
  | 'layout.nav.hub.matching'
  | 'layout.nav.hub.rules'
  | 'layout.nav.hub.title'
  | 'layout.nav.hub.subtitle'
  | 'layout.nav.syrveIiko'
  | 'layout.nav.organization'
  | 'layout.nav.organizationUsers'
  | 'layout.nav.platform'
  | 'layout.nav.connections'
  | 'layout.nav.syncStatus'
  | 'layout.nav.references'
  | 'layout.nav.bankAccounts'
  | 'layout.nav.settings'
  | 'layout.nav.telegram'
  | 'layout.nav.bankAdapters'
  | 'layout.nav.platformOrganizations'
  | 'layout.nav.platformUsers'
  | 'layout.nav.platformSettings'
  | 'layout.nav.workers'
  | 'layout.nav.logout'
  | 'layout.nav.changeContext'
  | 'layout.nav.openNavigation'
  | 'layout.sidebar.collapse'
  | 'layout.sidebar.expand'
  | 'dashboard.loading'
  | 'dashboard.error'
  | 'dashboard.contextRequired.title'
  | 'dashboard.contextRequired.message'
  | 'dashboard.title'
  | 'dashboard.subtitle'
  | 'dashboard.actions.upload'
  | 'dashboard.actions.unmatched'
  | 'dashboard.actions.ready'
  | 'dashboard.cards.newImports.title'
  | 'dashboard.cards.newImports.description'
  | 'dashboard.cards.unmatched.title'
  | 'dashboard.cards.unmatched.description'
  | 'dashboard.cards.lowConfidence.title'
  | 'dashboard.cards.lowConfidence.description'
  | 'dashboard.cards.ready.title'
  | 'dashboard.cards.ready.description'
  | 'dashboard.cards.failedPostings.title'
  | 'dashboard.cards.failedPostings.description'
  | 'dashboard.cards.successfulPostings.title'
  | 'dashboard.cards.successfulPostings.description'
  | 'dashboard.activity.title'
  | 'dashboard.activity.subtitle'
  | 'dashboard.syrveSync.title'
  | 'dashboard.syrveSync.subtitle'
  | 'dashboard.syrveSync.incremental'
  | 'dashboard.syrveSync.fullSuppliers'
  | 'dashboard.syrveSync.noConnection'
  | 'dashboard.syrveSync.successIncremental'
  | 'dashboard.syrveSync.successFullSuppliers'
  | 'dashboard.syrveSync.error'
  | 'dashboard.syrveSync.inProgress'
  | 'dashboard.syrveSync.inProgressFull'
  | 'notifications.ariaLabel'
  | 'notifications.slaLabel'
  | 'notifications.loading'
  | 'notifications.empty'
  | 'notifications.allItems'
  | 'notifications.severity.Info'
  | 'notifications.severity.Warning'
  | 'notifications.severity.Critical'
  | 'notifications.owner'
  | 'notifications.ownerUnassigned'
  | 'notifications.escalatedAt'
  | 'notifications.slaDue'
  | 'notifications.open'
  | 'notifications.assignMe'
  | 'notifications.acknowledge'
  | 'notifications.resolve'
  | 'notifications.dismiss'
  | 'notifications.markRead'
  | 'notifications.allAnomalies'
  | 'notifications.sla.Open'
  | 'notifications.sla.Acknowledged'
  | 'notifications.sla.Escalated'
  | 'notifications.sla.Resolved'
  | 'notifications.sla.Dismissed'
  | 'reports.reconciliationEvidence.csvButton'
  | 'reports.reconciliationEvidence.subtitle';

export type TranslationKey =
  | BaseTranslationKey
  | keyof typeof adminTranslations.ru
  | keyof typeof allocationsTranslations.ru
  | keyof typeof anomaliesTranslations.ru
  | keyof typeof auditTranslations.ru
  | keyof typeof authTranslations.ru
  | keyof typeof bankTranslations.ru
  | keyof typeof commonTranslations.ru
  | keyof typeof correctionsTranslations.ru
  | keyof typeof matchingTranslations.ru
  | keyof typeof paymentCandidatesTranslations.ru
  | keyof typeof platformTranslations.ru
  | keyof typeof settingsTranslations.ru
  | keyof typeof sharedTranslations.ru
  | keyof typeof statusTranslations.ru
  | keyof typeof apiTranslations.ru
  | keyof typeof syrveTranslations.ru
  | keyof typeof workbenchV1Translations.ru
  | keyof typeof workbenchV2Translations.ru
  | keyof typeof transactionsTranslations.ru;

export type TranslationMap = Record<TranslationKey, string>;

export type TranslateParams = Record<string, string | number>;
